import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Guid, isNotEmptyArray, isNotNil, Nillable } from '@web/utils';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, switchMap } from 'rxjs';
import { AccordionModule } from 'primeng/accordion';
import { GalleriaModule } from 'primeng/galleria';
import { WarehouseReleaseDetailsAttributesComponent } from '../../components/warehouse-release-details-attributes/warehouse-release-details-attributes.component';
import { WarehouseReleaseDetailsProductAttributesComponent } from '../../components/warehouse-release-details-product-attributes/warehouse-release-details-product-attributes.component';
import { WarehouseReleaseDataSourceService } from '../../services/warehouse-release-data-source.service';
import { WarehouseReleaseProduct } from '../../models/warehouse-release-product';
import { WarehouseReleaseDetails } from '../../models/warehouse-release-details';
import {
  DetailsViewComponent,
  NoDataComponent,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { MultimediaGalleriaThumbnailsComponent } from '@web/shared/module/multimedia';
import { WarehouseMultimediaDataSourceService } from '../../services/warehouse-multimedia-data-source.service';

@Component({
  selector: 'app-warehouse-release-details',
  imports: [
    AccordionModule,
    CommonModule,
    DetailsViewComponent,
    GalleriaModule,
    MultimediaGalleriaThumbnailsComponent,
    NoDataComponent,
    SpinnerFilledComponent,
    WarehouseReleaseDetailsAttributesComponent,
    WarehouseReleaseDetailsProductAttributesComponent,
  ],
  templateUrl: './warehouse-release-details.component.html',
  styleUrl: './warehouse-release-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseReleaseDetailsComponent {
  /* prettier-ignore */
  private readonly warehouseReleaseDataSource = inject(WarehouseReleaseDataSourceService);
  /* prettier-ignore */
  private readonly warehouseMultimediaDataSource = inject(WarehouseMultimediaDataSourceService);

  public readonly id = input.required<Guid>();
  public readonly thumbnailsVisibility = input(false);

  protected readonly id$ = toObservable(this.id);
  protected readonly details$ = this.detailsSource();

  public shouldShowProductAccordions(
    products: Nillable<WarehouseReleaseProduct[]>,
  ): products is WarehouseReleaseProduct[] {
    return isNotNil(products) && isNotEmptyArray(products);
  }

  /* prettier-ignore */
  protected readonly fetchMultimediaDetailsFn = (id: Guid) => this.warehouseMultimediaDataSource.fetchDetailsById(id, true);

  private detailsSource(): Observable<WarehouseReleaseDetails> {
    return this.id$.pipe(
      switchMap((id) => this.warehouseReleaseDataSource.fetchDetailsById(id)),
    );
  }
}
